import type { UniversalWorkReadQuery } from "@excellent/ui/api/contracts/universal-verification";

export type CanonicalUniversalWorkReadQuery = Extract<
  UniversalWorkReadQuery,
  { originTenantId: string }
>;

import {
  canonicalEnvironmentAvailable,
  type CanonicalEnvironmentContextValue,
} from "../../canonical-environment-provider";
import { hrefWithCanonicalEnvironmentSelection } from "../../environment-navigation";

export function canonicalWorkReadQuery(
  context: CanonicalEnvironmentContextValue | null | undefined,
  now = Date.now(),
): CanonicalUniversalWorkReadQuery | null {
  if (!canonicalEnvironmentAvailable(context, now)) return null;
  const subject = context?.response?.presentation?.subject;
  if (!subject || context?.selection.kind !== "environment") return null;
  if (
    context.selection.subject.originTenantId !== subject.originTenantId ||
    context.selection.subject.environmentId !== subject.environmentId ||
    context.selection.subject.generationId !== subject.generationId
  )
    return null;
  return { ...subject };
}

export function canonicalWorkResponseMatches(
  response: { environmentReadScope?: unknown } | null | undefined,
  query: UniversalWorkReadQuery | null,
  now = Date.now(),
): boolean {
  if (!response || !query || !("originTenantId" in query)) return false;
  const scope = response.environmentReadScope;
  if (!scope || typeof scope !== "object") return false;
  const value = scope as {
    mode?: unknown;
    locality?: unknown;
    validUntil?: unknown;
    subject?: Partial<typeof query>;
  };
  return (
    value.mode === "canonical-environment" &&
    ["local", "remote", "offline-snapshot"].includes(value.locality as string) &&
    typeof value.validUntil === "string" &&
    Number.isFinite(Date.parse(value.validUntil)) &&
    Date.parse(value.validUntil) > now &&
    value.subject?.originTenantId === query.originTenantId &&
    value.subject.environmentId === query.environmentId &&
    value.subject.generationId === query.generationId
  );
}

export function canonicalWorkHref(
  to: string,
  context: CanonicalEnvironmentContextValue | null | undefined,
): string {
  return context?.selection.kind === "environment"
    ? hrefWithCanonicalEnvironmentSelection(to, context.selection)
    : to;
}
